"use client";

import { useChat } from "@ai-sdk/react";
import { Icon, Text, Button } from "@zapier/design-system";

import {
  CopilotInput,
  type CopilotInputAction,
} from "@zapier/design-system-ai";
import styles from "./TemplateDetailCopilot.module.css";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import type { Template } from "@zapier/templates";

type TemplateCopilotPrompt = {
  label: string;
  onClick?: () => void;
};

type TemplateDetailCopilotProps = {
  template: Template;
  prompts: TemplateCopilotPrompt[];
  setIsCopilotOpen: (isOpen: boolean) => void;
};

export const TemplateDetailCopilot = ({
  template,
  prompts = [],
  setIsCopilotOpen,
}: TemplateDetailCopilotProps) => {
  const { input, handleInputChange, handleSubmit, setInput } = useChat({
    api: "/api/templates/copilot",
    body: { temperature: 0 },
  });

  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      emitUserInteractionEvent(
        UserInteractionEventName.templateDetailCopilotFocused,
        {
          event_action: "click",
          option_selected: "",
          custom_tracking_fields: {
            template_id: template.id,
          },
        },
      );
      setIsFocused(true);
    };

    input.addEventListener("focus", handleFocus);

    return () => {
      input.removeEventListener("focus", handleFocus);
    };
  }, []);

  const onSubmit = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotSubmitted,
      {
        event_action: "click",
        option_selected: `Submitted prompt: ${input}`,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    handleSubmit();
    setIsCopilotOpen(true);
  };

  const handleCollapse = () => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotCollapsed,
      {
        event_action: "click",
        option_selected: "",
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );
    setIsFocused(false);
  };

  const handlePromptClick = (prompt: TemplateCopilotPrompt) => {
    emitUserInteractionEvent(
      UserInteractionEventName.templateDetailCopilotPromptSelected,
      {
        event_action: "click",
        option_selected: prompt.label,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );

    setInput(prompt.label);

    if (prompt.onClick) {
      prompt.onClick();
    }
  };

  const inputActions: CopilotInputAction[] = [
    {
      id: "view-full-chat",
      location: "start",
      icon: <></>,
      onClick: (id) => {
        setIsCopilotOpen(true);
        console.log("View full chat clicked", id);
      },
      text: "View full chat",
    },
  ];

  return (
    <div className={styles.copilotContainer}>
      <div className={styles.copilotHeader}>
        <div className={styles.copilotTitle}>
          <Icon name="miscCopilot" size={22} color="BrandOrange" />
          <Text type="Body2">Copilot</Text>
        </div>
        {isFocused && (
          <div
            className={classNames(styles.copilotCollapse, {
              [styles.focused]: isFocused,
            })}
          >
            <Button color="ghost" size="small" onClick={handleCollapse}>
              Collapse
            </Button>
          </div>
        )}
      </div>
      {isFocused && prompts.length > 0 && (
        <ul
          className={classNames(styles.copilotPrompts, {
            [styles.focused]: isFocused,
          })}
        >
          {prompts.map((prompt) => (
            <li key={prompt.label}>
              <Button
                key={prompt.label}
                color="tertiary"
                size="small"
                onClick={() => handlePromptClick(prompt)}
              >
                {prompt.label}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <CopilotInput
        ref={inputRef}
        input={input}
        handleInputChange={handleInputChange}
        handleSubmit={onSubmit}
        actions={isFocused ? inputActions : []}
        placeholder="Ask about this template…"
        className={styles.copilotInput}
      />
    </div>
  );
};
